import {
  CaretDownIcon,
  CaretUpIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import { Button } from '@/components/ui/button';
import { PDFViewer } from '@/components/common/pdf-viewer';
import { ExcelDocViewer } from '@/components/xlsx-viewer/ExcelDocViewer';
import { ImageWithSmartScaling } from '@/components/common/ImageWithSmartScaling';
import { cn } from '@/lib/utils';

export interface DocumentItemData {
  id: string;
  documentId?: string;
  fileName: string;
  sizeBytes: number;
  mimeType?: string | null;
  s3Url: string;
  sourceEmailSubject?: string;
}

interface DocumentItemProps {
  document: DocumentItemData;
  isExpanded: boolean;
  isHighlighted: boolean;
  onToggleExpanded: () => void;
  onRefreshData: () => void;
}

const isExcelFile = (doc: { fileName: string; mimeType?: string | null }) => {
  const excelMimeTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.spreadsheet',
  ];
  const excelExtensions = ['.xls', '.xlsx', '.csv', '.ods'];

  if (doc.mimeType && excelMimeTypes.includes(doc.mimeType)) return true;
  return excelExtensions.some((ext) =>
    doc.fileName.toLowerCase().endsWith(ext),
  );
};

const isPdfFile = (doc: { fileName: string; mimeType?: string | null }) => {
  return (
    doc.mimeType === 'application/pdf' ||
    doc.fileName.toLowerCase().endsWith('.pdf')
  );
};

const isImageFile = (doc: { mimeType?: string | null }) => {
  return doc.mimeType?.startsWith('image/');
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function DocumentItem({
  document,
  isExpanded,
  isHighlighted,
  onToggleExpanded,
  onRefreshData,
}: DocumentItemProps) {
  return (
    <div
      className={cn(
        'rounded-md border transition-all',
        isHighlighted
          ? 'border-primary border-2 bg-primary/5'
          : 'border-border',
      )}
    >
      {/* Collapsed header - always visible */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30"
        onClick={onToggleExpanded}
      >
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">
            {document.fileName}
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <span>{formatFileSize(document.sizeBytes)}</span>
            {document.sourceEmailSubject && (
              <>
                <span>-</span>
                <span className="truncate">
                  <T context="document source email subject prefix">
                    from: <Var>{document.sourceEmailSubject}</Var>
                  </T>
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              window.open(document.s3Url, '_blank');
            }}
          >
            <ArrowSquareOutIcon className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {isExpanded ? (
              <CaretUpIcon className="h-4 w-4" />
            ) : (
              <CaretDownIcon className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="p-3 pt-0 border-t">
          {isImageFile(document) ? (
            <ImageWithSmartScaling
              src={document.s3Url}
              alt={document.fileName}
              fileName={document.fileName}
              sizeBytes={document.sizeBytes}
            />
          ) : isPdfFile(document) ? (
            <PDFViewer
              url={document.s3Url}
              pageMode="single"
              onUrlExpired={onRefreshData}
            />
          ) : isExcelFile(document) ? (
            <ExcelDocViewer
              url={document.s3Url}
              fileName={document.fileName}
              documentId={document.documentId}
            />
          ) : (
            <div className="text-center py-4">
              <a
                href={document.s3Url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline text-sm hover:text-primary/80"
              >
                <T context="download link for a file">
                  Download <Var>{document.fileName}</Var>
                </T>
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
